import { take } from 'rxjs';

import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { UserStore } from '@cxc/core/services';
import { FiltersService } from '@services/API/trusts/operations/filters/filters.service';

@Injectable({
  providedIn: 'root',
})
export class LenderState {
  private filtersService = inject(FiltersService);
  private userStore = inject(UserStore);

  lenders = signal<string[]>([]);
  selected = signal<string | undefined>(undefined);
  selected$ = toObservable(this.selected);

  /**
   * Which user the current `lenders` belong to, set before the request goes
   * out rather than when it lands.
   *
   * Two things this has to get right:
   *
   * - LEN-2067 proposed guarding on `lenders().length`, but that only closes
   *   the door once a response has arrived. The three General Overview
   *   components that used to call this did so in the same tick, so all three
   *   would still have passed a post-response guard. Guarding on intent is
   *   what collapses the burst.
   * - It is keyed by user, not a plain boolean. `UserStore.logOut()` navigates
   *   with the router and never reloads the page, so this root-provided state
   *   survives a logout; a permanent flag would leave the next user looking at
   *   the previous one's lenders.
   */
  private loadedFor: string | undefined;

  /**
   * Forgets which user the current `lenders` were loaded for, so the next
   * `setFromAPI()` fetches again. Called when a facility is created elsewhere
   * (LEN-2587): its lender or originator may not be in the list yet. The
   * current list stays in place until that next call.
   */
  invalidate() {
    this.loadedFor = undefined;
  }

  setFromAPI() {
    /**
     * The following code is an optimization.
     *
     * If the user is not STAFF then they don't need to call the API
     * for their own lender, we already got that from the /me endpoint
     * and it's in the localStorage
     */
    const user = this.userStore.get();
    const userLender = user.entity?.name!;
    const isStaff = userLender === 'CXC';
    // Falls back to the entity when there is no email, so the guard still has
    // something to key on.
    const userKey = user.email || userLender;

    // Purely local and free, so it stays outside the guard: a repeat call must
    // still put a non-staff user's own lender back in place.
    if (!isStaff) {
      this.selected.set(userLender);
    }

    if (this.loadedFor === userKey) {
      return;
    }

    // A different user than the one the current list belongs to. Drop it
    // rather than let it show through until the response lands.
    this.loadedFor = userKey;
    this.lenders.set([]);

    if (isStaff) {
      this.selected.set(undefined);
    }

    this.filtersService
      .get()
      .pipe(take(1))
      .subscribe({
        next: response => {
          const lenders = response.lenders.map(lender => lender.value);

          this.lenders.set(lenders);

          if (!isStaff) {
            return;
          }

          // Is Staff, assing first of array
          if (lenders.length) {
            this.selected.set(lenders[0]);
          }
        },
        // Reopen the door on failure: a permanent flag would leave the page
        // with no lenders for the rest of the session after one bad response.
        error: () => {
          this.loadedFor = undefined;
        },
      });
  }
}
